/**
 * Edit Mode State Hook
 * 
 * Manages floor plan edit mode state and operations
 * Date: 09-10-2025 12:40:09
 * 
 * @module floor-plan-editor/useEditMode
 */

import { useState, useCallback, useEffect } from 'react';
import type { EditModeState, LayoutData } from '../../../types/room-layout';

/**
 * Local storage key for unsaved changes
 */
const UNSAVED_CHANGES_KEY = 'mchd_floor_plan_unsaved_changes';

/**
 * Hook for managing edit mode state
 * 
 * @param floorId - Current floor ID
 * @returns Edit mode state and operations
 */
export function useEditMode(floorId: string | null) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Map<string, LayoutData>>(new Map());
  const [originalLayouts, setOriginalLayouts] = useState<Map<string, LayoutData>>(new Map());

  /**
   * Calculate if there are unsaved changes
   */
  const hasUnsavedChanges = pendingChanges.size > 0;

  /**
   * Enter edit mode
   */
  const enterEditMode = useCallback(() => {
    setIsEditMode(true);
    setPendingChanges(new Map());
    setOriginalLayouts(new Map());
    setSelectedRoomId(null);
  }, []);

  /**
   * Exit edit mode and discard changes
   */
  const exitEditMode = useCallback(() => {
    // Check for unsaved changes
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to exit Edit Mode?'
      );
      if (!confirmed) {
        return false;
      }
    }

    setIsEditMode(false);
    setSelectedRoomId(null);
    setPendingChanges(new Map());
    setOriginalLayouts(new Map());
    
    // Clear localStorage
    if (floorId) {
      localStorage.removeItem(`${UNSAVED_CHANGES_KEY}_${floorId}`);
    }

    return true;
  }, [hasUnsavedChanges, floorId]);

  /**
   * Update room layout locally
   * 
   * @param roomId - Room ID to update
   * @param layout - New layout data
   */
  const updateRoomLayout = useCallback((roomId: string, layout: LayoutData) => {
    setPendingChanges(prev => {
      const updated = new Map(prev);
      updated.set(roomId, layout);
      return updated;
    });

    // Save original layout if not already saved
    setOriginalLayouts(prev => {
      if (!prev.has(roomId)) {
        const updated = new Map(prev);
        updated.set(roomId, layout);
        return updated;
      }
      return prev;
    });
  }, []);

  /**
   * Select a room for transformation
   * 
   * @param roomId - Room ID to select (null to deselect)
   */
  const selectRoom = useCallback((roomId: string | null) => {
    setSelectedRoomId(roomId);
  }, []);

  /**
   * Undo changes for a specific room
   * 
   * @param roomId - Room ID to undo changes for
   */
  const undoRoomChanges = useCallback((roomId: string) => {
    setPendingChanges(prev => {
      const updated = new Map(prev);
      updated.delete(roomId);
      return updated;
    });
  }, []);

  /**
   * Undo all changes and restore original layouts
   */
  const undoAllChanges = useCallback(() => {
    setPendingChanges(new Map());
    setSelectedRoomId(null);
  }, []);

  /**
   * Get all pending changes as array
   * 
   * @returns Array of layout data with changes
   */
  const getPendingChanges = useCallback((): LayoutData[] => {
    return Array.from(pendingChanges.values());
  }, [pendingChanges]);

  /**
   * Save pending changes to localStorage (browser crash protection)
   */
  useEffect(() => {
    if (!floorId || !hasUnsavedChanges) return;

    const changes = Array.from(pendingChanges.entries());
    localStorage.setItem(
      `${UNSAVED_CHANGES_KEY}_${floorId}`,
      JSON.stringify(changes)
    );
  }, [floorId, pendingChanges, hasUnsavedChanges]);

  /**
   * Restore unsaved changes from localStorage on mount
   */
  useEffect(() => {
    if (!floorId) return;

    const stored = localStorage.getItem(`${UNSAVED_CHANGES_KEY}_${floorId}`);
    if (stored) {
      try {
        const changes = JSON.parse(stored);
        const restoredMap = new Map(changes);
        
        // Ask user if they want to restore
        const confirmed = window.confirm(
          'You have unsaved layout changes from a previous session. Would you like to restore them?'
        );

        if (confirmed) {
          setPendingChanges(restoredMap);
        } else {
          localStorage.removeItem(`${UNSAVED_CHANGES_KEY}_${floorId}`);
        }
      } catch (error) {
        console.error('Failed to restore unsaved changes:', error);
        localStorage.removeItem(`${UNSAVED_CHANGES_KEY}_${floorId}`);
      }
    }
  }, [floorId]);

  return {
    isEditMode,
    selectedRoomId,
    pendingChanges,
    hasUnsavedChanges,
    enterEditMode,
    exitEditMode,
    updateRoomLayout,
    selectRoom,
    undoRoomChanges,
    undoAllChanges,
    getPendingChanges
  };
}


